/**
 * /health — unauthenticated probe for uptime monitors.
 *
 * Two real checks against the configured Supabase project:
 *   - GoTrue  GET /auth/v1/health (public, no key)
 *   - PostgREST HEAD count on a customer-safe view via the anon key. RLS may
 *     return zero rows to anon; an HTTP error is what fails the check.
 * Nothing here is hardcoded "ok".
 */
import { useEffect, useState } from 'react';
import { SUPABASE_URL, supabase } from '../lib/supabase';
import { errorMessage } from '../lib/errors';

interface Check {
  name: string;
  ok: boolean;
  latency_ms: number | null;
  detail: string;
}

interface HealthResult {
  checks: Check[];
  checked_at: string;
}

const PROBE_TIMEOUT_MS = 8000;

async function checkAuth(): Promise<Check> {
  const started = performance.now();
  try {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/health`, { signal: AbortSignal.timeout(PROBE_TIMEOUT_MS) });
    const latency = Math.round(performance.now() - started);
    const body = (await res.json().catch(() => null)) as { version?: string } | null;
    return {
      name: 'supabase_auth',
      ok: res.ok,
      latency_ms: latency,
      detail: res.ok ? `GoTrue ${body?.version ?? 'reachable'}` : `HTTP ${res.status}`,
    };
  } catch (e) {
    return { name: 'supabase_auth', ok: false, latency_ms: null, detail: errorMessage(e) };
  }
}

async function checkRest(): Promise<Check> {
  const started = performance.now();
  try {
    const { error, status } = await supabase
      .from('unifi_sites_customer_view')
      .select('id', { head: true, count: 'exact' })
      .abortSignal(AbortSignal.timeout(PROBE_TIMEOUT_MS));
    const latency = Math.round(performance.now() - started);
    return {
      name: 'supabase_rest',
      ok: !error,
      latency_ms: latency,
      detail: error ? `${error.message}${error.code ? ` (${error.code})` : ''}` : `PostgREST responded (HTTP ${status})`,
    };
  } catch (e) {
    return { name: 'supabase_rest', ok: false, latency_ms: null, detail: errorMessage(e) };
  }
}

async function runChecks(): Promise<HealthResult> {
  if (!SUPABASE_URL) {
    const detail = 'VITE_SUPABASE_URL is not set';
    return {
      checks: [
        { name: 'supabase_auth', ok: false, latency_ms: null, detail },
        { name: 'supabase_rest', ok: false, latency_ms: null, detail },
      ],
      checked_at: new Date().toISOString(),
    };
  }
  const checks = await Promise.all([checkAuth(), checkRest()]);
  return { checks, checked_at: new Date().toISOString() };
}

export function HealthPage() {
  const [result, setResult] = useState<HealthResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    void runChecks().then((r) => {
      if (!cancelled) setResult(r);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const status = !result ? 'checking' : result.checks.every((c) => c.ok) ? 'ok' : 'degraded';
  const payload = {
    status,
    portal: 'onsite',
    supabase_url: SUPABASE_URL || null,
    checked_at: result?.checked_at ?? null,
    checks: result?.checks ?? [],
  };

  return (
    <main className="min-h-[var(--paz-vh,100vh)] bg-bg p-4 text-text">
      <h1 className="text-lg font-bold">On-Site Portal health</h1>
      <p className="mt-1 text-sm" role="status" aria-live="polite">
        Status:{' '}
        <span className={status === 'ok' ? 'font-semibold text-success' : status === 'degraded' ? 'font-semibold text-error' : 'text-text-muted'}>
          {status}
        </span>
      </p>
      {result && (
        <ul className="mt-3 space-y-1 text-sm">
          {result.checks.map((c) => (
            <li key={c.name}>
              <span className={c.ok ? 'text-success' : 'text-error'}>{c.ok ? 'PASS' : 'FAIL'}</span>{' '}
              <span className="font-mono">{c.name}</span>
              {c.latency_ms !== null ? ` · ${c.latency_ms} ms` : ''} · {c.detail}
            </li>
          ))}
        </ul>
      )}
      <pre id="health-json" className="mt-4 overflow-x-auto rounded-md bg-surface-2 p-3 font-mono text-xs">
        {JSON.stringify(payload, null, 2)}
      </pre>
    </main>
  );
}
